import styled from '@emotion/styled'
import { AnimatedLabel } from 'app/components/Common/Animation/AnimatedLabel'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button } from 'app/components/Common/Button'
import {
  Props as SlideProps,
  Slide,
} from 'app/components/ExperiencesSlider/Slide'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import { uniqBy } from 'lodash'
import React, { memo } from 'react'

export interface Props {
  description?: string
  experiencesPageURL?: string
  items: SlideProps[]
  label?: string
  languageCode: string
  title?: string
}

export const ExperiencesPreview = memo(function ExperiencesPreview({
  description,
  experiencesPageURL,
  items,
  label,
  languageCode,
  title,
}: Props) {
  if (items.length < 1) {
    return null
  }

  return (
    <Container>
      <Head dial={9} row space="between" wrap>
        <LeftSide>
          {label ? <Label variant="light" text={label} /> : null}

          {title ? <Title delay={0.3} length={50} text={title} /> : null}

          {description ? (
            <FadeInUp>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}
        </LeftSide>

        {experiencesPageURL ? (
          <FadeInUp>
            <CTA
              label={useVocabularyData('find-out', languageCode)}
              URL={experiencesPageURL}
              variant="full-light"
            />
          </FadeInUp>
        ) : null}
      </Head>

      <List row stretch wrap>
        {uniqBy(items, 'title').map((item, index) => (
          <FadeInUp delay={index * 0.1} key={index}>
            <Slide variant="light" {...item} />
          </FadeInUp>
        ))}
      </List>
    </Container>
  )
})

const Container = styled.section`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  margin-top: 7.5rem;
  padding: 7.5rem 10vw;

  @media (max-width: 1199px) {
    margin-top: 5.625rem;
    padding: 3.75rem 1.875rem;
  }
`

const Head = styled(FlexBox)``

const LeftSide = styled.div`
  width: 55%;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const Label = styled(AnimatedLabel)``

const Title = styled(AnimatedTitle)`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.625rem;
  font-weight: 500;
  line-height: 2.625rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1.875rem;
    line-height: 1.875rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 1.25rem;
`

const CTA = styled(Button)`
  @media (max-width: 1199px) {
    margin-top: 1.875rem;
  }
`

const List = styled(FlexBox)`
  gap: 5.25rem 4.166vw;
  margin-top: 3rem;

  > div {
    width: calc(50% - 2.083vw);
  }

  @media (max-width: 1199px) {
    display: block;

    > div {
      width: 100%;
      margin-top: 3.75rem;

      .bg {
        aspect-ratio: 1.5;
        height: auto;
      }
    }
  }
`
